import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import TestDriveIcon from './TestDriveIcon';
import WalkaroundIcon from './WalkaroundIcon';
import PricingIcon from './PricingIcon';
import { UserCoverImage, WidgetConfig } from '../types';
import { getAssetURL } from '../utils/url';

interface RightSidePanelProps {
  config: WidgetConfig,
  vehicleImageUrl?: UserCoverImage;
  onTry?: () => void;
  onClose?: () => void;
}

const RightSidePanel: React.FC<RightSidePanelProps> = ({
  config,
  vehicleImageUrl,
  onTry,
  onClose,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        right: { xs: 8, sm: 16 },
        top: { xs: 80, sm: '50%' },
        transform: { xs: 'none', sm: 'translateY(-50%)' },
        zIndex: 2147483645,
        width: { xs: 80, sm: 150 },
        px: { xs: 0, sm: 1.5 },
        py: { xs: 0, sm: 1.5 },
        borderRadius: { xs: '12px', sm: 2 },
        bgcolor: '#171a1f',
        background:
          'linear-gradient(180deg, rgba(27,32,38,0.98) 0%, rgba(16,18,21,0.98) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 12px 24px rgba(0,0,0,0.35)',
        overflow: 'visible',
      }}
    >
      {onClose && (
        <IconButton
          aria-label='Close right side panel'
          onClick={() => onClose()}
          size='small'
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            transform: 'translate(50%, -50%)',
            zIndex: 1,
            width: {xs: 20, sm: 24},
            height:{xs: 20, sm:  24},
            p: 0,
            color: '#ffffff',
            bgcolor: 'rgba(27,32,38,0.98)',
            border: '1px solid rgba(255,255,255,0.18)',
            '&:hover': {
              bgcolor: 'rgba(27,32,38,0.70)',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      <Stack spacing={{ xs: 2, sm: 1.25 }} alignItems={{ xs: 'center', sm: 'stretch' }}>
        {/* NEW Badge/Image - Mobile shows badge, Desktop shows image */}
        <Box sx={{ display: { xs: 'flex', sm: 'flex' }, justifyContent: 'center' }}>
          <Box
            component={'img'}
            src='https://miles-cdn.qrbuddy.ai/static/ic_new.png'
            sx={{
              width: { xs: '80%', sm: '50%' },
              mt: {xs: 1,sm: 0}
            }}
          />
        </Box>

        {/* Divider after NEW image */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', mt: { xs: -2, sm: 0 }, display: {xs: 'none', sm: 'block'} }} />

        {/* Desktop: Text content */}
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Typography
            sx={{
              color: '#fff',
              fontSize: '9px',
              lineHeight: 1.1,
              textAlign: 'center',
            }}
          >
            New Interactive Experience
          </Typography>
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: '15px',
              lineHeight: 1.1,
              textAlign: 'center',
              whiteSpace: 'pre-line',
            }}
          >
            {'Explore\nInventory'}
          </Typography>
        </Box>

        {/* Desktop: Vehicle Image */}
        <Box
          sx={{
            height: 72,
            borderRadius: 1.2,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            bgcolor: '#0d0f12',
            display: { xs: 'none', sm: 'block' }
          }}
        >
          <Box
            component='img'
            src={vehicleImageUrl ? getAssetURL(config.assetsUrl, vehicleImageUrl.key, vehicleImageUrl.url) : 'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_web_rail_3_cadillac_cars.png'}
            alt='Vehicle'
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>

        {/* Divider */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', mt: { xs: -2, sm: 0 }, display: {xs: 'none', sm: 'block'} }} />

        {/* Action Icons */}
        <Stack spacing={{ xs: 2.5, sm: 1 }} sx={{ py: { xs: 1, sm: 0 } }}>
          <PanelAction
            label='Test Drive'
            icon={<TestDriveIcon fontSize='large' />}
          />
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', mt: { xs: -2, sm: 0 } }} />
          <PanelAction
            label='Walkaround'
            icon={<WalkaroundIcon fontSize='large' />}
          />
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', mt: { xs: -2, sm: 0 } }} />
          <PanelAction
            label='Pricing'
            icon={<PricingIcon fontSize='large' />}
          />
        </Stack>

        {/* Divider */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', mt: { xs: -2, sm: 0 }, display: {xs: 'none', sm: 'block'} }} />

        {/* Button - Different text for mobile/desktop */}
        <Button
          onClick={onTry}
          fullWidth
          variant='contained'
          sx={{
            background: 'linear-gradient(to right, #355D87, #103367)',
            mt: { xs: 0, sm: 1.5 },
            fontSize: { xs: '13px', sm: '0.769rem' },
            textTransform: 'none',
            fontWeight: { xs: 600, sm: 700 },
            borderRadius: {xs: 0, sm: 1.5},
            bgcolor: '#3a3f46',
            color: '#ffffff',
            '&:hover': { bgcolor: '#4a5058' },
            px: 1,
            py: { xs: 1, sm: 0.75 },
            minHeight: { xs: '36px', sm: 'auto' },
            boxShadow: { xs: 'none', sm: 'initial' },
          }}
        >
          View Inventory
        </Button>
      </Stack>
    </Paper>
  );
};

interface PanelActionProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

const PanelAction: React.FC<PanelActionProps> = ({ icon, label, onClick }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 0, sm: 1 },
        p: { xs: 0, sm: 0.5 },
        borderRadius: 1.5,
        textTransform: 'none',
        color: { xs: '#ffffff', sm: '#dfe6ee' },
      }}
    >
      <Box
        sx={{
          width: { xs: 40, sm: 36 },
          height: { xs: 40, sm: 36 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: 1.1,
          textAlign: 'center',
          display: { xs: 'none', sm: 'block' },
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default RightSidePanel;
